"use client";

import { useTranslations } from "next-intl";
import type { ComponentPropsWithoutRef } from "react";
import {
  Controller,
  type FieldPath,
  type FieldValues,
  useFormContext,
} from "react-hook-form";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

interface RHFBirthdaySelectProps<TFieldValues extends FieldValues>
  extends Omit<
    ComponentPropsWithoutRef<typeof Controller>,
    "name" | "control" | "render"
  > {
  dayName: FieldPath<TFieldValues>;
  monthName: FieldPath<TFieldValues>;
  yearName: FieldPath<TFieldValues>;
  containerClass?: string;
  startYear?: number;
  endYear?: number;
}

// Month VALUES are stored data ("1".."12"); only the displayed labels are
// localized via `common.months.{value}` (resolved in the component).
const MONTH_VALUES = [
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "11",
  "12",
] as const;

function generateDays() {
  return Array.from({ length: 31 }, (_, i) => ({
    label: String(i + 1),
    value: String(i + 1),
  }));
}

function generateYears(start: number, end: number) {
  const years = [];
  for (let y = end; y >= start; y--) {
    years.push({ label: String(y), value: String(y) });
  }
  return years;
}

export default function RHFBirthdaySelect<TFieldValues extends FieldValues>({
  dayName,
  monthName,
  yearName,
  containerClass,
  startYear = 1310,
  endYear = 1395,
}: RHFBirthdaySelectProps<TFieldValues>) {
  const { control } = useFormContext();
  const t = useTranslations("common");

  const days = generateDays();
  const years = generateYears(startYear, endYear);
  const months = MONTH_VALUES.map((value) => ({
    value,
    label: t(`months.${value}`),
  }));

  const selectContentClassName =
    "bg-bg-secondary rounded-sm border-stroke-subtle shadow-[0px_2px_4px_-2px_#0000001A,0px_4px_6px_-1px_#0000001A]";

  return (
    <div className={cn("flex items-center w-full", containerClass)}>
      <Controller
        name={dayName}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Select onValueChange={field.onChange} value={field.value || ""}>
            <SelectTrigger
              className={cn(
                "w-full rounded-e-none",
                error && "border-semantic-error",
              )}
              size="default"
            >
              <SelectValue placeholder={t("form.day")} />
            </SelectTrigger>
            <SelectContent
              side="bottom"
              className={cn(selectContentClassName, "max-h-60")}
            >
              {days.map((d) => (
                <SelectItem
                  key={d.value}
                  value={d.value}
                  className="font-medium rounded-sm"
                >
                  {d.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      />

      <Controller
        name={monthName}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Select onValueChange={field.onChange} value={field.value || ""}>
            <SelectTrigger
              className={cn(
                "w-full rounded-none -ms-px",
                error && "border-semantic-error",
              )}
              size="default"
            >
              <SelectValue placeholder={t("form.month")} />
            </SelectTrigger>
            <SelectContent
              side="bottom"
              className={cn(selectContentClassName, "max-h-60")}
            >
              {months.map((m) => (
                <SelectItem
                  key={m.value}
                  value={m.value}
                  className="font-medium rounded-sm"
                >
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      />

      <Controller
        name={yearName}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Select onValueChange={field.onChange} value={field.value || ""}>
            <SelectTrigger
              className={cn(
                "w-full rounded-s-none -ms-px",
                error && "border-semantic-error",
              )}
              size="default"
            >
              <SelectValue placeholder={t("form.year")} />
            </SelectTrigger>
            <SelectContent
              side="bottom"
              className={cn(selectContentClassName, "max-h-60")}
            >
              {years.map((y) => (
                <SelectItem
                  key={y.value}
                  value={y.value}
                  className="font-medium rounded-sm"
                >
                  {y.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      />
    </div>
  );
}
